import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, NgZone, ChangeDetectorRef } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, of as observableOf, Subject, Subscription } from 'rxjs';

import { take, takeUntil } from 'rxjs/operators';

import { AppRouterFacadeService } from '@purplefront/app-router/data-access';
import { CatalogService } from '@purplefront/catalog/data-access';

import * as fromCatalogStore from '../../../../../../catalog/data-access/src/lib/_store';
import * as fromBookmarksStore from '../../../../../../bookmarks/data-access/src/lib/_store';
import * as fromAppRouterStore from '../../../../../../app-router/data-access/src/lib/_store';

import { Catalog, Feed } from '@purplefront/catalog/data-access';
import { Bookmark } from '@purplefront/bookmarks/data-access';
import { Langs, Page } from '@purplefront/shared';
import * as fromPreferencesStore from '@purplefront/preferences/data-access';

interface FeedSubPage {
  type: Page;
  slug: string;
  label: string;
}

const defaultPages: FeedSubPage[] = [
  { type: 'dashboard', slug: 'dashboard', label: 'Dashboard' },
  { type: 'reader', slug: 'reader', label: 'My Reader' },
  { type: 'radar', slug: 'radar', label: 'My Radar' },
  { type: 'afprelax', slug: 'afprelax', label: 'My AFP & Relaxnews Content' },
  // { type: 'dataviz', slug: 'dataviz', label: 'Dataviz' },
  { type: 'calendar', slug: 'calendar', label: 'My Augmented Agenda' }
];

@Component({
  selector: 'app-select-menu',
  templateUrl: './select-menu.component.html',
  styleUrls: ['./select-menu.component.scss']
})
export class SelectMenuComponent implements OnInit, OnDestroy {
  @Input() bookmarkCount: number;
  @Input() userFeeds$: Observable<Feed[]>;
  @Output() getFolders$ = new EventEmitter<any>();

  public pages$: Observable<FeedSubPage[]>;
  public lang$: Observable<Langs>;
  private _ngUnsubscribe$: Subject<void> = new Subject<void>();

  public catalogs: Catalog[] | null;
  public folders$: Observable<any>;
  public searchListItems: Bookmark[];
  public currentFeedSlug: string | null;
  public currentFolderId: string | null;
  public currentPageType: string | null;
  public currentFeedId: string | null;
  public userFeeds: Feed[];
  public lang: string;
  public searchString: string;
  public searchId: string;
  public activeRouteSubscription: Subscription;

  constructor(
    private catalogStore: Store<fromCatalogStore.CatalogState>,
    private bookmarkStore: Store<fromBookmarksStore.BookmarksState>,
    private appRouterStore: Store<fromAppRouterStore.AppRouterState>,
    private _preferencesStore: Store<fromPreferencesStore.PreferencesState>,
    private appRouterFacade: AppRouterFacadeService,
    private catalogService: CatalogService,
    private _ngZone: NgZone,
    private _ref: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.userFeeds$.pipe(takeUntil(this._ngUnsubscribe$)).subscribe((feeds) => {
      this.userFeeds = feeds;
    });
    /**
     * Get lang & current route from appRouterStore
     */

    this.appRouterStore
      .select(fromAppRouterStore.selectContentLang)
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe((_lang) => (this.lang = _lang));

    this.appRouterStore
      .pipe(select(fromAppRouterStore.selectSearchString))
      .subscribe((search) => (this.searchString = search));

    this.appRouterStore
      .pipe(select(fromAppRouterStore.selectSavedSearchId))
      .subscribe((searchId) => (this.searchId = searchId));

    /**
     * Get current route & check for page type
     */

    this.appRouterStore
      .select(fromAppRouterStore.selectPage)
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe((_page) => (this.currentPageType = _page));

    /**
     * Get page list
     */

    this.pages$ = observableOf(defaultPages);

    /**
     * Set current folder id
     */
    this.appRouterStore
      .pipe(take(1), select(fromAppRouterStore.selectFolderId))
      .subscribe((id) => (this.currentFolderId = id));

    /**
     * Get current catalog
     */

    this.catalogStore
      .select(fromCatalogStore.catalogs)
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe((catalogs) => {
        if (catalogs) {
          this.catalogs = catalogs;
        }
      });

    /**
     * Get current feed
     */

    this._preferencesStore
      .select(fromPreferencesStore.currentFeedSlug)
      .pipe(takeUntil(this._ngUnsubscribe$))
      .subscribe((feedSlug) => (this.currentFeedSlug = feedSlug));

    this.bookmarkStore
      .pipe(takeUntil(this._ngUnsubscribe$), select(fromBookmarksStore.selectSearchBookmarks))
      .subscribe((searchListItems) => (this.searchListItems = searchListItems));
  }

  /**
   * Get feed folder when opening menu
   */

  getFolders(feed) {
    this.getFolders$.emit({ feed: feed, lang: this.lang });
    this.folders$ = this.catalogStore.select(fromCatalogStore.folders);
  }

  /**
   * Go to feed list page
   */

  goToFeedList(): void {
    this.appRouterFacade.goToFeedList();
  }

  /**
   * Go to specific feed page
   */

  goToFeed(feed: Feed): void {
    this.appRouterFacade.goToFeed({ feed: feed.slug });
  }

  /**
   * Go to specific page
   */

  goToPage(feed: Feed, page: FeedSubPage): void {
    // this.appRouterService.goToFeedPage(feed.slug, page.slug);
  }

  goToBookmarkPage() {
    this.appRouterFacade.gotoBookmarksPage();
  }

  goToAddFeedPage() {
    this.appRouterFacade.gotoAddFeedPage();
  }

  isVisibleFeed(userFeeds: Feed[], feed: Feed): boolean {
    return this.catalogService.isVisible(userFeeds, feed.slug);
  }

  goToOnDemandPage() {
    this.appRouterFacade.goToOrderOnDemandPage();
  }

  goToFolderPage(folderId: string) {
    this.appRouterFacade.goToFolderPage(folderId);
  }

  /**
   * Prevent memory leaks
   */

  ngOnDestroy() {
    this._ngUnsubscribe$.next();
    this._ngUnsubscribe$.complete();
  }
}
